import { FC, useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@components/layout/DashboardLayout';
import { PropertyTable } from '@components/properties/PropertyTable';
import { PropertyFilterBar, ViewMode } from '@components/properties/PropertyFilterBar';
import { PropertyCard } from '@components/properties/PropertyCard';
import { Property, PropertyStatus } from '@/types/index';
import { propertyService } from '@services/property.service';

const PropertyMap = lazy(() => import('@components/properties/PropertyMap').then(m => ({ default: m.PropertyMap })));

const STORAGE_KEY = 'immohelvet_rejected_view_mode';

export const Rejected: FC = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState<Property[]>([]);
  const [filteredProperties, setFilteredProperties] = useState<Property[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>(
    () => (localStorage.getItem(STORAGE_KEY) as ViewMode | null) ?? 'cards'
  );

  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    try {
      setIsLoading(true);
      const data = await propertyService.getAllFiltered({
        status: PropertyStatus.REJECTED,
      });
      setProperties(data.properties);
    } catch (error) {
      console.error('Erreur lors du chargement des annonces rejetées:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditProperty = async (property: Property) => {
    try {
      const freshProperty = await propertyService.getById(property.id);
      navigate(`/analysis/${freshProperty.id}`);
    } catch {
      navigate(`/analysis/${property.id}`);
    }
  };

  const handleDeleteProperty = async (id: string) => {
    try {
      await propertyService.delete(id);
      await loadProperties();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression de la propriété');
    }
  };

  const handleRestoreProperty = async (property: Property) => {
    try {
      await propertyService.update(property.id, { status: PropertyStatus.FIRST_SELECTION });
      await loadProperties();
    } catch (error) {
      console.error('Erreur lors de la restauration:', error);
      alert('Erreur lors de la restauration de la propriété');
    }
  };

  const handlePromoteProperty = async (property: Property) => {
    try {
      await propertyService.update(property.id, { status: PropertyStatus.OPPORTUNITY });
      await loadProperties();
    } catch (error) {
      console.error('Erreur lors de la promotion:', error);
    }
  };

  return (
    <DashboardLayout title="Rejetées">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              Rejetées
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Annonces ne répondant pas aux critères de sélection. Utilisez ↩️ pour restaurer en 1ère sélection.
            </p>
          </div>
        </div>

        {/* Filtres */}
        <PropertyFilterBar
          properties={properties}
          onFilterChange={setFilteredProperties}
          viewMode={viewMode}
          onViewModeChange={(m) => { setViewMode(m); localStorage.setItem(STORAGE_KEY, m); }}
        />

        {/* Vue tableau */}
        {viewMode === 'table' && (
          <PropertyTable
            properties={filteredProperties}
            onEdit={handleEditProperty}
            onDelete={handleDeleteProperty}
            onRestore={handleRestoreProperty}
            isLoading={isLoading}
          />
        )}

        {/* Vue cards */}
        {viewMode === 'cards' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {isLoading
              ? Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-32 bg-gray-100 dark:bg-gray-700 rounded-xl animate-pulse" />
                ))
              : filteredProperties.map(p => (
                  <PropertyCard
                    key={p.id}
                    property={p}
                    onEdit={handleEditProperty}
                    onRestore={handleRestoreProperty}
                    onPromote={handlePromoteProperty}
                  />
                ))
            }
          </div>
        )}

        {/* Vue carte */}
        {viewMode === 'map' && (
          <Suspense fallback={<div className="h-[600px] bg-gray-100 dark:bg-gray-700 rounded-xl animate-pulse" />}>
            <PropertyMap properties={filteredProperties} />
          </Suspense>
        )}
      </div>
    </DashboardLayout>
  );
};
